import { today } from '@components/ArtBoard/Export/helpers'
import type { ID } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import formatXML from 'xml-formatter'

import { useGetDataForExport } from '@/hooks/exportDropdown'
import type { ExportData } from '@/hooks/exportDropdown/types'
import {
  getHexCode,
  getProjectXml,
  type XmlExportColorItem,
  type XmlExportSectionItem,
} from '@/utils/xml'
import type { UseControlsReducerState } from '../types'

export const useSubmitExportAsXml = (state: UseControlsReducerState) => {
  const projectId = useSelector(migrationProjectSelector.getProjectId)
  const projectTitle = useSelector(migrationProjectSelector.getProjectTitle)
  const colors = useSelector(migrationProjectSelector.getProjectColors)
  const defaultColorId = useSelector(
    migrationProjectSelector.getProjectDefaultColorId
  )
  const tags = useSelector(migrationProjectSelector.getProjectTags)

  const projectSections = useSelector(
    migrationProjectSelector.getProjectSections
  )

  const { getExportData, getSectionIds } = useGetDataForExport()
  const exportData = getExportData()
  const sectionIds = getSectionIds()

  const sectionTitleById = useMemo<Map<ID, string>>(
    () =>
      new Map(projectSections.map((section) => [section.id, section.title])),
    [projectSections]
  )

  const hexCodeById = useMemo<Map<ID, string>>(
    () => new Map(colors.map((color) => [color.id, getHexCode(color.hexCode)])),
    [colors]
  )

  const getColorHexCode = (colorId: ID): string => {
    if (!colorId || colorId === defaultColorId) {
      return ''
    }

    return hexCodeById.get(colorId) || ''
  }

  const tagTitlesByNodeId = useMemo<Map<ID, string[]>>(() => {
    const titlesByNodeId = new Map<ID, string[]>()

    tags.forEach((tag) => {
      tag.nodes.forEach((nodeId) => {
        titlesByNodeId.set(nodeId, [
          ...(titlesByNodeId.get(nodeId) || []),
          tag.title,
        ])
      })
    })

    return titlesByNodeId
  }, [tags])

  const getNodeTagTitles = (nodeId: ID): string[] =>
    tagTitlesByNodeId.get(nodeId) || []

  const tagTitlesByColorHexCode = useMemo<Map<string, string[]>>(() => {
    const titlesByHexCode = new Map<string, string[]>()

    tags.forEach((tag) => {
      const hexCode = getColorHexCode(tag.colorId)
      titlesByHexCode.set(hexCode, [
        ...(titlesByHexCode.get(hexCode) || []),
        tag.title,
      ])
    })

    return titlesByHexCode
  }, [tags, hexCodeById, defaultColorId])

  const exportSections = useMemo<XmlExportSectionItem[]>(() => {
    if (sectionIds.length === 0) {
      return [{ title: '', nodes: exportData.flat() }]
    }

    return sectionIds.map((sectionId, index) => ({
      title: sectionTitleById.get(sectionId) || '',
      nodes: exportData[index] || [],
    }))
  }, [sectionIds, sectionTitleById, exportData])

  // Цвет по умолчанию в атрибутах узлов не пишется, но в <colors> он нужен,
  // если стоит в легенде — иначе при обратном импорте легенда потеряется.
  const exportColors = useMemo<XmlExportColorItem[]>(() => {
    const usedColorIds = new Set<ID>()

    const collectNodeColorIds = (node: ExportData) => {
      if (node.colorId) {
        usedColorIds.add(node.colorId)
      }

      node.blocks?.forEach((block) => {
        if (block.colorId) {
          usedColorIds.add(block.colorId)
        }
      })

      node.children?.forEach(collectNodeColorIds)
    }

    exportSections.forEach((section) => {
      section.nodes.forEach(collectNodeColorIds)
    })
    tags.forEach((tag) => {
      if (tag.colorId) {
        usedColorIds.add(tag.colorId)
      }
    })

    return colors
      .filter(
        (color) =>
          color.showInLegend ||
          (color.id !== defaultColorId && usedColorIds.has(color.id))
      )
      .map((color) => ({
        hexCode: getHexCode(color.hexCode),
        title: color.title,
        showInLegend: color.showInLegend,
      }))
      .filter((color) => Boolean(color.hexCode))
  }, [colors, defaultColorId, exportSections, tags])

  const result = useMemo<string>(
    () =>
      getProjectXml({
        state,
        projectTitle,
        projectId,
        publishDate: today(),
        sections: exportSections,
        tagTitlesByColorHexCode,
        colors: exportColors,
        getColorHexCode,
        getNodeTagTitles,
      }),
    [
      state,
      projectTitle,
      projectId,
      exportSections,
      tagTitlesByColorHexCode,
      exportColors,
      hexCodeById,
      defaultColorId,
      tagTitlesByNodeId,
    ]
  )

  const formattedResult = useMemo<string>(
    () => formatXML(result, { collapseContent: true }),
    [result]
  )

  const handleSubmit = () => {
    return Promise.resolve(
      new Blob([formattedResult], { type: 'application/xml;charset=utf-8' })
    )
  }

  return {
    handleSubmit,
    result: formattedResult,
    size: formattedResult.split('\n').length,
  }
}
